import type { Skier } from '../physics/skier'
import type { InputFrame } from '../physics/skier'

/**
 * The race. A time trial down a designed slope.
 *
 * The clock is the whole score now, so everything that used to be judged is
 * simply priced in seconds: a crash costs its 1.5s of lost momentum plus every
 * second spent rebuilding speed, deep snow ploughs, a terrain trap bleeds
 * everything you had, and hesitating on loose steep snow lets your own sluff
 * catch you. Nothing needs a rule to punish it — the clock already does.
 */

export type RaceState = 'ready' | 'running' | 'finished'

export interface Split {
  /** Downhill distance of the line, metres. */
  readonly z: number
  /** Elapsed seconds when it was crossed, or null if not yet. */
  time: number | null
}

export class Race {
  state: RaceState = 'ready'
  /** Elapsed seconds. Frozen once finished. */
  time = 0
  readonly splits: Split[]
  /** Ticks elapsed — the authoritative clock; `time` is derived. */
  ticks = 0

  private nextSplit = 0

  constructor(
    checkpointZ: readonly number[],
    private readonly finishZ: number,
    private readonly startZ: number,
  ) {
    this.splits = checkpointZ.map((z) => ({ z, time: null }))
  }

  reset(): void {
    this.state = 'ready'
    this.time = 0
    this.ticks = 0
    this.nextSplit = 0
    for (const s of this.splits) s.time = null
  }

  /**
   * Advance one fixed tick. Counted in ticks rather than accumulated floats so
   * two runs with the same input stream produce byte-identical times — which is
   * what makes a leaderboard defensible.
   */
  step(skier: Skier, input: InputFrame, dt: number): void {
    if (this.state === 'finished') return

    if (this.state === 'ready') {
      // The clock starts when you commit: any input, or simply sliding off the
      // start shelf. Standing in the gate is free.
      //
      // Tuck is deliberately not "acting" any more. It used to be a held
      // gesture and so a statement of intent, but it is automatic now — true
      // from the first tick with the thumb at rest — so counting it started
      // the clock the instant the page loaded, before the player had done
      // anything at all. Every posted time would have quietly included
      // however long someone sat looking at the gate.
      const moved = skier.z > this.startZ + 1.5 || skier.speed > 1.2
      const acted = input.steer !== 0 || input.brake || input.pop
      if (!moved && !acted) return
      this.state = 'running'
    }

    this.ticks++
    this.time = this.ticks * dt

    while (this.nextSplit < this.splits.length && skier.z >= this.splits[this.nextSplit].z) {
      this.splits[this.nextSplit].time = this.time
      this.nextSplit++
    }

    if (skier.z >= this.finishZ) this.state = 'finished'
  }

  get finished(): boolean {
    return this.state === 'finished'
  }

  /** Index of the last split crossed, or -1. */
  get lastSplitIndex(): number {
    return this.nextSplit - 1
  }
}

/** mm:ss.mmm, or --:--.--- when there is no time yet. */
export function formatTime(seconds: number | null): string {
  if (seconds == null) return '--:--.---'
  const m = Math.floor(seconds / 60)
  const s = seconds - m * 60
  return `${m}:${s.toFixed(3).padStart(6, '0')}`
}

/** Signed delta, e.g. "-1.284" or "+0.907". */
export function formatDelta(seconds: number | null): string {
  if (seconds == null) return '—'
  return `${seconds >= 0 ? '+' : '-'}${Math.abs(seconds).toFixed(3)}`
}
